import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../systems/api-client';
import styles from './darwinx-dashboard.module.css';

interface ScormPackage {
  id: string;
  scenario_id: string;
  title: string;
  created_by_email: string | null;
  created_at: string;
  revoked_at: string | null;
}

interface PackagesResponse {
  packages?: ScormPackage[];
  available?: boolean;
  unavailableReason?: string | null;
  error?: string;
}

interface Props {
  enterprises: { id: string; name: string }[];
  scenarios: { id: string; title: string; version?: string; workspaceId: string | null }[];
}

/** SQLite returns "YYYY-MM-DD HH:MM:SS" in UTC, PostgreSQL an ISO string. */
function formatDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('fr-FR');
}

function fileNameFrom(disposition: string | null, fallback: string): string {
  const match = disposition?.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const ScormPackagesPanel: React.FC<Props> = ({ enterprises, scenarios }) => {
  const [workspaceId, setWorkspaceId] = useState('');
  const [packages, setPackages] = useState<ScormPackage[]>([]);
  const [unavailableReason, setUnavailableReason] = useState<string | null>(null);
  const [scenarioId, setScenarioId] = useState('');
  const [title, setTitle] = useState('');
  const [generating, setGenerating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const workspaceScenarios = scenarios.filter((scenario) => scenario.workspaceId === workspaceId);

  const loadPackages = useCallback(async (enterpriseId: string) => {
    setPackages([]);
    setUnavailableReason(null);
    if (!enterpriseId) return;
    try {
      const response = await apiFetch(`/api/scorm/admin/enterprises/${enterpriseId}/packages`);
      const data = (await response.json()) as PackagesResponse;
      if (!response.ok) {
        alert(data.error || 'Impossible de charger les paquets SCORM');
        return;
      }
      setPackages(data.packages ?? []);
      setUnavailableReason(data.available === false ? data.unavailableReason ?? 'Indisponible' : null);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    setScenarioId('');
    setTitle('');
    void loadPackages(workspaceId);
  }, [workspaceId, loadPackages]);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceId || !scenarioId) return;
    setGenerating(true);
    try {
      const response = await apiFetch(`/api/scorm/admin/enterprises/${workspaceId}/packages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenarioId, title: title.trim() || undefined }),
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { error?: string };
        alert(data.error || 'Échec de la génération du paquet');
        return;
      }
      const fileName = fileNameFrom(
        response.headers.get('Content-Disposition'),
        `cybernovel-${scenarioId}-scorm2004.zip`
      );
      downloadBlob(await response.blob(), fileName);
      setTitle('');
      await loadPackages(workspaceId);
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la génération du paquet');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (pkg: ScormPackage) => {
    if (
      !window.confirm(
        `Révoquer le paquet « ${pkg.title} » ? Les apprenants qui le lancent depuis le LMS ne pourront plus se connecter.`
      )
    ) {
      return;
    }
    setRevokingId(pkg.id);
    try {
      const response = await apiFetch(
        `/api/scorm/admin/enterprises/${workspaceId}/packages/${pkg.id}/revoke`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { error?: string };
        alert(data.error || 'Échec de la révocation');
      }
      await loadPackages(workspaceId);
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la révocation');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>Paquets SCORM (LMS)</h2>
      </div>
      <p className={styles.instruction}>
        Génère un ZIP SCORM 2004 à déposer dans le LMS du client. Les apprenants sont connectés
        automatiquement avec l&apos;identifiant transmis par le LMS : un compte est créé à leur premier
        lancement. La clé du paquet n&apos;est jamais réaffichée : en cas de perte, générez un nouveau
        paquet et révoquez l&apos;ancien. Réservé aux workspaces sans SSO.
      </p>
      <div className={styles.whitelistForm}>
        <select value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} className={styles.input}>
          <option value="">Sélectionner un workspace</option>
          {enterprises.map((enterprise) => (
            <option key={enterprise.id} value={enterprise.id}>
              {enterprise.name}
            </option>
          ))}
        </select>
      </div>

      {!workspaceId ? (
        <p className={styles.instruction}>Choisissez un workspace pour gérer ses paquets SCORM.</p>
      ) : (
        <>
          {unavailableReason ? (
            <p className={styles.instruction}>{unavailableReason}</p>
          ) : (
            <form onSubmit={handleGenerate} className={`${styles.whitelistForm} ${styles.scormForm}`}>
              <select
                value={scenarioId}
                onChange={(e) => setScenarioId(e.target.value)}
                className={styles.input}
                disabled={workspaceScenarios.length === 0 || generating}
                required
              >
                <option value="">
                  {workspaceScenarios.length === 0 ? 'Aucune histoire liée à ce workspace' : 'Choisir le scénario'}
                </option>
                {workspaceScenarios.map((scenario) => (
                  <option key={scenario.id} value={scenario.id}>
                    {scenario.version ? `${scenario.title} (${scenario.version})` : scenario.title} — {scenario.id}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Titre dans le LMS (facultatif)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={styles.input}
                maxLength={200}
                disabled={generating}
              />
              <button type="submit" className={styles.addBtn} disabled={!scenarioId || generating}>
                {generating ? 'Génération…' : 'Générer le ZIP'}
              </button>
            </form>
          )}

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Scénario</th>
                  <th>Créé le</th>
                  <th>Par</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {packages.length === 0 ? (
                  <tr>
                    <td colSpan={6}>Aucun paquet</td>
                  </tr>
                ) : (
                  packages.map((pkg) => (
                    <tr key={pkg.id}>
                      <td>{pkg.title}</td>
                      <td><code>{pkg.scenario_id}</code></td>
                      <td>{formatDate(pkg.created_at)}</td>
                      <td>{pkg.created_by_email ?? '—'}</td>
                      <td>{pkg.revoked_at ? `Révoqué le ${formatDate(pkg.revoked_at)}` : 'Actif'}</td>
                      <td>
                        {!pkg.revoked_at && (
                          <button
                            type="button"
                            className={styles.actionBtn}
                            disabled={revokingId === pkg.id}
                            onClick={() => handleRevoke(pkg)}
                          >
                            {revokingId === pkg.id ? '…' : 'Révoquer'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

export default ScormPackagesPanel;
